import { Link } from 'react-router-dom'
import { formatDuration, formatServings, selectRecipeMetaVisibility, useAppSelector, useT, DIFFICULTY_LABEL_KEYS, type Recipe } from '@reciperoll/core'
import { Clock, Search, ChevronRight, Check, Star } from 'react-feather'
import FavouriteButton from './Favourites/FavouriteButton'
import { RecipeImage } from './RecipeImage'

interface RecipeListItemProps {
  recipe: Recipe
  // Ingredient name(s) that matched a search — shown highlighted under the meta.
  matches?: string[]
  // Whether this recipe has an in-progress cook (shown as a left accent bar).
  isCooking?: boolean
}

export function RecipeListItem({ recipe, matches, isCooking = false }: RecipeListItemProps) {
  const t = useT()
  const showMeta = useAppSelector(selectRecipeMetaVisibility)
  const servings = showMeta.servings ? formatServings(recipe.servings, t) : null

  return (
    <Link
      to={`/recipe/${recipe.id}`}
      className="group relative flex items-stretch rounded-2xl overflow-hidden bg-surface border border-line shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 [content-visibility:auto] [contain-intrinsic-size:auto_100px]"
    >
      {/* Subtle in-progress cue — full-height bar flush to the left edge. */}
      {isCooking && (
        <span className="absolute left-0 top-0 bottom-0 w-1 bg-terracotta z-10" />
      )}

      {/* Full-height image, flush to the card's left edge. */}
      <div className="relative shrink-0 w-24 self-stretch bg-terracotta-wash">
        <RecipeImage recipe={recipe} className="absolute inset-0" />
        {recipe.cooked && (
          <span className="absolute top-1.5 right-1.5 w-5 h-5 rounded-full bg-green-500 flex items-center justify-center border-2 border-surface">
            <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
          </span>
        )}
      </div>

      {/* Content sits to the right of the image, with its own padding. */}
      <div className="flex-1 min-w-0 flex items-center gap-3 px-4 py-5">
        <div className="flex-1 min-w-0">
          <p className="text-[15px] font-semibold text-ink mb-0.5 truncate group-hover:text-terracotta-ink transition-colors">
            {recipe.title}
          </p>
          {/* Duration (with clock), difficulty, servings — each omitted when N/A. */}
          <div className="flex items-center gap-2.5 text-xs font-medium text-ink-faint">
            {showMeta.duration && !!recipe.cookingDuration && (
              <span className="flex items-center gap-1 tabular-nums">
                <Clock className="w-3.5 h-3.5 text-terracotta" />
                {formatDuration(recipe.cookingDuration, t)}
              </span>
            )}
            {showMeta.difficulty && recipe.difficulty && <span>{t(DIFFICULTY_LABEL_KEYS[recipe.difficulty])}</span>}
            {servings && <span>{servings}</span>}
            {showMeta.rating && recipe.rating != null && (
              <span className="flex items-center gap-1 tabular-nums">
                <Star className="w-3.5 h-3.5 text-terracotta fill-terracotta" />
                {recipe.rating}
              </span>
            )}
          </div>
          {matches?.length ? (
            <div className="flex items-center gap-1 mt-1 min-w-0">
              <Search className="w-3 h-3 shrink-0 text-terracotta" />
              <span className="text-xs font-medium text-terracotta truncate">{matches.join(", ")}</span>
            </div>
          ) : null}
        </div>

        <FavouriteButton
          recipe={recipe}
          className="w-8 h-8 rounded-lg flex items-center justify-center shrink-0 hover:bg-surface-2 transition-colors"
        />

        <ChevronRight
          className="w-4 h-4 text-ink-faint shrink-0 group-hover:text-ink-soft transition-colors"
          aria-hidden="true"
        />
      </div>
    </Link>
  )
}
